/**
 * Dependencies.
 */
import { debounce, flatten, orderBy, uniqBy } from 'lodash-es';
import Moment from 'moment-timezone';
import { Component, Mixins, Watch } from 'vue-property-decorator';

import DeliveryRequestMessageModel from '@/api/order-request-message/order-request-message.model';
import { orderRequestMessageService } from '@/api/order-request-message/order-request-message.service';
import { orderRequestService } from '@/api/order-request/order-request.service';
import {
  ORDER_REQUEST_MESSAGE_SOURCES,
  ORDER_REQUEST_TYPES
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: { Error, NotifyClientRequest, Timeline },
  name: 'request-message-chat-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `source`.
   */
  source = ORDER_REQUEST_MESSAGE_SOURCES;

  /**
   * Property `types`.
   */
  types = ORDER_REQUEST_TYPES;

  /**
   * Property `isLoadingMessages`.
   */
  isLoadingMessages = false;

  /**
   * Property `isLoadingMessages`.
   */
  isSnackbarVisible = false;

  /**
   * Property `isNewMessageBageVisible`.
   */
  isNewMessageBageVisible = false;

  /**
   * Property `message`.
   */
  message = null;

  /**
   * Property `historyMessages`.
   */
  historyMessages = [];

  /**
   * Property `currentPendingMessages`.
   */
  currentPendingMessages = [];

  /**
   * Property `currentMessage`.
   */
  currentMessage = null;

  /**
   * Property `isDeleteMessageModalVisible`.
   */
  isDeleteMessageModalVisible = false;

  /**
   * Property `deleteMessages`.
   */
  deleteMessages = [];

  /**
   * Property `readMessages`.
   */
  readMessages = [];

  /**
   * Property `isSendingMessage`.
   */
  isSendingMessage = false;

  /**
   * Getter `messages`.
   */
  get messages() {
    const { historyMessages } = this || {};

    // Remove deleted messages arrived.
    let { storedChatMessages } = trackingStore || {};

    storedChatMessages = storedChatMessages.filter(
      ({ source }) => source !== 'DELETED' && source !== 'READED'
    );

    let messages = [
      ...orderBy([...historyMessages], ['recordDate'], ['asc']),
      ...storedChatMessages
    ];

    messages = messages.map(message => {
      const { id, isDeleted, isRead = false } = message || {};
      return {
        ...message,
        isDeleted: this.deleteMessages.includes(id) ? true : isDeleted,
        isRead: this.readMessages.includes(id) ? true : isRead
      };
    });

    return uniqBy(messages, 'id');
  }

  /**
   * Getter window resize debounced.
   */
  get windowResizeDebounced() {
    return debounce(this.handleScrollToMessagesBottom, 100);
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', async () => {
      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      } else {
        await this.handleInitializeRequestMessages();
      }
    });

    await this.handleInitializeMixin();

    const isValid = this.handleIsValidToBeOnScene();

    if (!isValid) {
      this.handleForceTrackingScene();
    } else {
      await this.handleInitializeRequestMessages();

      window.addEventListener('resize', this.windowResizeDebounced);
    }
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    const { storedChatMessages = [] } = trackingStore || {};
    if (storedChatMessages.length > 0 && !this.hasRequestMessage) {
      this.handleGetDeliveryData();
    }

    trackingStore.resetChatMessages();

    window.removeEventListener('resize', this.windowResizeDebounced);
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateRequest && this.isDelivery;
  }

  /**
   * Handle initialize request messages.
   */
  async handleInitializeRequestMessages() {
    this.isLoadingMessages = true;

    trackingStore.resetChatMessages();

    await this.handleGetHistoryMessages();

    this.handleScrollToMessagesBottom();

    this.handleSetMessageRead();

    this.isLoadingMessages = false;
  }

  /**
   * Handle set message read.
   */
  async handleSetMessageRead() {
    const id = this.messageOrderRequestId;

    if (id) {
      await orderRequestService.setMessageRead(id);
    }
  }

  /**
   * Handle get last history messages.
   */
  async handleGetHistoryMessages() {
    const requests = trackingStore.requests || [];
    const requestsOfTypeMessage = requests.filter(
      ({ type }) => type === this.types.MESSAGE
    );

    const historyMessages = [];
    requestsOfTypeMessage.forEach(({ id }) => {
      historyMessages.push(
        this.handleGetRequestMessages({
          deliveryRequestId: id
        })
      );
    });

    const history = await Promise.all(historyMessages);
    this.historyMessages = flatten(history);
  }

  /**
   * Handle send message.
   */
  async handleSendMessage() {
    const { message = null } = this || {};

    if (message && !this.isSendingMessage) {
      this.isSendingMessage = true;

      const { collect, delivery } = trackingStore || {};
      const { id: deliveryId = null } = delivery || {};
      const { id: collectId = null } = collect || {};

      const messageData = new DeliveryRequestMessageModel({
        ...{
          [this.isDelivery ? 'deliveryId' : 'collectId']: this.isDelivery
            ? deliveryId
            : collectId
        },
        deliveryRequestId: this.messageOrderRequestId,
        isRead: false,
        message,
        orderType: this.isDelivery ? 'DELIVERY' : 'COLLECT',
        recordDate: this.moment().toISOString(),
        source: this.source.CUSTOMER
      });

      try {
        const response = await orderRequestMessageService.post(messageData);
        const { result: id } = (response as any) || {};

        if (!this.hasRequestMessage) {
          await this.handleGetDeliveryData();
        }

        trackingStore.addChatMessage({
          ...messageData,
          id
        });

        this.message = null;
      } catch (error) {
        if (process.env.BUILD_MODE !== 'prd') {
          console.error(error);
        }
      } finally {
        this.handleScrollToMessagesBottom();

        this.isSendingMessage = false;
      }
    }
  }

  /**
   * Handle scroll to messages bottom.
   */
  handleScrollToMessagesBottom() {
    this.$nextTick().then(() => {
      const container = this.$refs['messages'] as HTMLElement;

      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  /**
   * Handle new message badge.
   */
  handleNewMessageBadge() {
    this.handleScrollToMessagesBottom();
    this.isNewMessageBageVisible = false;
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.push({
      name: 'Tracking',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle delete message.
   */
  handleDeleteMessage({ message }) {
    this.isDeleteMessageModalVisible = true;
    this.currentMessage = message;
  }

  /**
   * Handle cancel delete message.
   */
  handleCancelDeleteMessage() {
    this.isDeleteMessageModalVisible = false;
    this.currentMessage = null;
  }

  /**
   * Handle confirm delete message.
   */
  async handleConfirmDeleteMessage() {
    const { id } = this.currentMessage || {};

    try {
      await orderRequestMessageService.delete(id);

      if (!this.deleteMessages.includes(id)) {
        this.deleteMessages.push(id);
      }
    } catch (error) {
      const { code = null } = error?.raw || {};

      if (code === 3234) {
        this.isSnackbarVisible = true;
      }

      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }
    } finally {
      this.isDeleteMessageModalVisible = false;
      this.currentMessage = null;
    }
  }

  /**
   * Handle chat message arrived.
   */
  @Watch('hasChatMessageArrived')
  async handleChatMessageArrived(hasArrived) {
    if (hasArrived) {
      // Deleted and readed messages.
      let { storedChatMessages = [] } = trackingStore || {};

      storedChatMessages = storedChatMessages.filter(
        ({ source }) => source === 'DELETED' || source === 'READED'
      );

      storedChatMessages.forEach(({ id, source }) => {
        if (id && source === 'DELETED' && !this.deleteMessages.includes(id)) {
          this.deleteMessages.push(id);
        }

        if (id && source === 'READED' && !this.deleteMessages.includes(id)) {
          this.readMessages.push(id);
        }
      });

      // Test if is at the bottom of the chat.
      const container = this.$refs['messages'] as HTMLElement;
      const isAtBottom =
        container.offsetHeight + container.scrollTop >= container.scrollHeight;

      // If at the bottom of the chat, stay there.
      if (isAtBottom) {
        this.handleScrollToMessagesBottom();
      } else {
        // If not at the bottom of the chat, show badge.
        this.isNewMessageBageVisible = true;

        setTimeout(() => {
          this.isNewMessageBageVisible = false;
        }, 3000);
      }

      // Mark as read.
      this.handleSetMessageRead();
      this.hasChatMessageArrived = false;
    }
  }
}
